import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import "./LabeledSliderDiscrete.css";

/**
 * A slider component that snaps to discrete values with labels
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Main label for the slider
 * @param {Array<{value: number, label: string}>} props.options - Array of discrete options with values and labels
 * @param {number} props.value - Current selected value
 * @param {Function} props.onChange - Function called during slider movement
 * @param {Function} props.onSnap - Function called when slider snaps to a discrete value
 * @param {string} [props.className] - Additional CSS class names
 */
const LabeledSliderDiscrete = ({
  label,
  options,
  value,
  onChange,
  onSnap,
  className = "",
  disabled = false,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  // Ensure options are sorted by value
  const sortedOptions = [...options].sort((a, b) => a.value - b.value);

  // Get min and max values from options
  const minValue = sortedOptions[0]?.value || 0;
  const maxValue = sortedOptions[sortedOptions.length - 1]?.value || 100;

  // Calculate percent position for each tick mark
  const getTickPosition = optionValue => {
    return ((optionValue - minValue) / (maxValue - minValue)) * 100;
  };

  // Find the closest snap option to a given value
  const findClosestOption = useCallback(
    val => {
      return sortedOptions.reduce((prev, curr) => {
        return Math.abs(curr.value - val) < Math.abs(prev.value - val)
          ? curr
          : prev;
      });
    },
    [sortedOptions]
  );

  // Handle slider value change
  const handleChange = e => {
    if (disabled) return;

    const newValue = parseFloat(e.target.value);
    setLocalValue(newValue);

    if (onChange) {
      onChange(newValue);
    }
  };

  // Handle start dragging
  const handleMouseDown = () => {
    if (disabled) return;
    setIsDragging(true);
  };

  // Handle end dragging - snap to closest value
  const handleMouseUp = useCallback(() => {
    if (disabled || !isDragging) return;

    const closestOption = findClosestOption(localValue);
    setLocalValue(closestOption.value);

    if (onSnap) {
      onSnap(closestOption.value, closestOption.label);
    }

    setIsDragging(false);
  }, [disabled, isDragging, localValue, onSnap, sortedOptions]);

  // Update local value when prop value changes (for external updates)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Add mouseup listener to document for cases where mouse is released outside the slider
  useEffect(() => {
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseUp]);

  return (
    <div
      className={`labeled-slider-discrete ${className} ${
        disabled ? "disabled" : ""
      }`}
    >
      <div className="labeled-slider-discrete__header">
        <label className="labeled-slider-discrete__label">{label}</label>
      </div>

      <div className="labeled-slider-discrete__slider-container">
        <input
          type="range"
          min={minValue}
          max={maxValue}
          step="any"
          value={localValue}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          disabled={disabled}
          className="labeled-slider-discrete__slider"
        />

        <div className="labeled-slider-discrete__tick-marks">
          {sortedOptions.map(option => (
            <div
              key={option.value}
              className="labeled-slider-discrete__tick-mark"
              style={{ left: `${getTickPosition(option.value)}%` }}
              onClick={
                disabled
                  ? undefined
                  : () => {
                      setLocalValue(option.value);
                      if (onSnap) {
                        onSnap(option.value, option.label);
                      }
                    }
              }
            >
              <div className="labeled-slider-discrete__tick-line"></div>
              <div className="labeled-slider-discrete__tick-label">
                {option.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

LabeledSliderDiscrete.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  onSnap: PropTypes.func.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

export default LabeledSliderDiscrete;
